import React from "react";
import { Link } from "react-router-dom";
import { Music, Users, Sparkles, ArrowRight } from "lucide-react";
import { MAGIC_PHOTOS } from "../data/cruiseData";

export default function MagicComesAlive() {
 return (
 <section id="experience" className="relative w-full overflow-hidden bg-blue-950 text-white" data-testid="magic-alive-section">
 {/* Background image + layered overlays */}
 <div className="absolute inset-0">
 <img
 src={MAGIC_PHOTOS.danceFloor}
 alt="Packed dance floor on Magic catamaran at night with DJ, purple lights, Sydney marina behind, guests dancing around the Magic logo banner"
 className="w-full h-full object-cover"
 />
 <div className="absolute inset-0 bg-gradient-to-r from-blue-950/85 via-blue-950/40 to-fuchsia-900/30" />
 <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 via-transparent to-blue-950/40" />
 <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] bg-fuchsia-500/30 rounded-full blur-3xl" />
 <div className="absolute -bottom-40 -left-40 w-[40rem] h-[40rem] bg-violet-500/30 rounded-full blur-3xl" />
 </div>

 <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
 <div className="grid md:grid-cols-12 gap-10 items-center">
 {/* LEFT */}
 <div className="md:col-span-7">
 <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-4 py-2 mb-6">
 <Music className="w-4 h-4 text-fuchsia-300" />
 <span className="text-xs font-black tracking-[0.2em]">REAL MAGIC ENERGY</span>
 </div>

 <h2 className="font-black text-4xl md:text-6xl lg:text-7xl leading-[1.05] mb-6 tracking-tight drop-shadow-2xl" data-testid="magic-alive-headline">
 When the lights go down,
 <span className="block bg-gradient-to-r from-fuchsia-300 via-pink-200 to-amber-200 bg-clip-text text-transparent italic font-light pb-2 md:pb-3">
 Magic comes alive.
 </span>
 </h2>

 <p className="text-lg md:text-xl text-white/90 max-w-2xl mb-8 leading-relaxed font-light">
 Live DJ, full dance floor, panoramic harbour views and 100 of your favourite people. This is what a night on <span className="font-semibold text-amber-200">Magic</span> really looks like, no staging, no models, just the real party.
 </p>

 <div className="grid grid-cols-3 gap-4 max-w-xl mb-10">
 <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
 <Users className="w-6 h-6 text-fuchsia-300 mb-2" />
 <div className="text-2xl md:text-3xl font-black">100</div>
 <div className="text-[10px] md:text-xs font-bold tracking-wider text-white/70">GUESTS</div>
 </div>
 <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
 <Music className="w-6 h-6 text-fuchsia-300 mb-2" />
 <div className="text-2xl md:text-3xl font-black">DJ</div>
 <div className="text-[10px] md:text-xs font-bold tracking-wider text-white/70">ON BOARD</div>
 </div>
 <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
 <Sparkles className="w-6 h-6 text-fuchsia-300 mb-2" />
 <div className="text-2xl md:text-3xl font-black">5hr</div>
 <div className="text-[10px] md:text-xs font-bold tracking-wider text-white/70">PARTY</div>
 </div>
 </div>

 <div className="flex flex-wrap gap-4">
 <Link
 to="/contact"
 className="group inline-flex items-center gap-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white px-8 py-4 rounded-full font-black text-base tracking-wide hover:shadow-2xl hover:shadow-fuchsia-500/50 hover:scale-105 transition-all"
 data-testid="magic-alive-book-btn"
 >
 BOOK YOUR PARTY
 <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
 </Link>
 <Link
 to="/gallery"
 className="bg-white/10 backdrop-blur-md border-2 border-white/40 text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white hover:text-blue-950 transition-all"
 data-testid="magic-alive-photos-btn"
 >
 See More Real Photos
 </Link>
 </div>
 </div>

 {/* RIGHT, Instagram polaroid with glow */}
 <div className="hidden md:block md:col-span-5">
 <div className="relative">
 <div className="absolute -inset-4 bg-gradient-to-br from-fuchsia-400/30 via-pink-400/20 to-amber-300/30 rounded-3xl blur-2xl" />
 <div className="relative bg-white rounded-2xl p-3 shadow-2xl rotate-[3deg]" data-testid="magic-alive-ig-card">
 <div className="flex items-center gap-2 px-2 py-2">
 <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-fuchsia-500 via-pink-500 to-amber-400 p-[2px]">
 <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
 <Music className="w-4 h-4 text-blue-950" />
 </div>
 </div>
 <div className="flex-1">
 <div className="text-[11px] font-black text-blue-950 tracking-tight">@magic.cruises</div>
 <div className="text-[9px] text-slate-500 font-medium">Sydney Harbour · Tonight</div>
 </div>
 <div className="text-slate-400 text-xl leading-none">⋯</div>
 </div>
 <div className="relative overflow-hidden rounded-lg aspect-[4/5]">
 <img
 src={MAGIC_PHOTOS.danceFloor}
 alt="Magic catamaran dance floor packed with guests at night"
 className="w-full h-full object-cover"
 />
 <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
 <span className="bg-black/70 backdrop-blur text-white text-[10px] font-black tracking-[0.2em] px-2.5 py-1 rounded-full">DANCE FLOOR</span>
 <span className="bg-fuchsia-500 text-white text-[10px] font-black px-2 py-1 rounded-full">LIVE</span>
 </div>
 </div>
 <div className="px-2 pt-2.5 pb-1 text-[10px] text-blue-950">
 <span className="font-black">@magic.cruises</span>{" "}
 <span className="text-slate-700">Magical moments, unforgettable experiences. #SydneyHarbour #MagicCruises</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>
 );
}
